import {
  EXCEL_MONEY_NUM_FMT,
  forecastStartMonth,
  formatMonthLabel,
} from './cashflowFeeExposureHelpers';
import { shownChartCustomer } from './pipelineExposure';

const ROAS_NUM_FMT = '0.00"%"';
const MAX_ROAS_PERIODS = 120;
const MODEL_NAVY = 'FF1F4E79';
const MODEL_WHITE = 'FFFFFFFF';
const MODEL_ACTUAL = 'FF70AD47';
const MODEL_PROJECTED = 'FF2E75B6';
const MODEL_TOTAL_FILL = 'FFD6E4F0';
const MODEL_GREEN_FILL = 'FFE2EFDA';
const MODEL_GREEN_FONT = 'FF375623';
const MODEL_GRAY_FILL = 'FFF2F2F2';
const MODEL_GRAY_FONT = 'FF595959';
const MODEL_MONEY_FMT = '#,##0;(#,##0);-';

const MODEL_SECTION_LABELS = new Set([
  '① CUSTOMER VIEW',
  'PIPELINE',
  'LIVE COMPANIES',
  'TOTAL',
  'FEE SUMMARY',
  '① CASH INFLOW',
  '② CASH OUTFLOW',
  '① EQUITY',
  '② SEED NOTES',
  '③ INSTITUTIONAL DEBT (to be activated)',
  '④ AR Facility (to be activated)',
]);

const MODEL_GRAY_LABELS = new Set([
  'ASSUMPTIONS',
  'SUMMARY',
  'CUMULATIVE',
  'CAPITAL EFFICIENCY METRICS',
  'Overheads',
]);

const MODEL_TOTAL_LABELS = new Set([
  'Net capital needed',
  'Total Fee (yield)',
  'Total Cash Inflow',
  'Total Cash Outflow',
  'Close Balance',
  'AUM - Based on Deployment ($K)',
  'Gross Revenue ($K)',
  'Total Professional Fees',
  'Total Rent&Office',
  'Total Overheads',
  'Total Portfolio AUM ($)',
  'Total Debt Balance ($)',
  'Total Interest Cost ($)',
  'Cumulative Interest Cost ($)',
]);

const MODEL_GREEN_LABELS = new Set([
  'Net Cash Flow',
  'Net Revenue ($K)',
  'Monthly Net Cash Flow',
  'Net Interest Margin ((Revenue - Cost) / AUM)',
]);

const MODEL_GRAY_TOTAL_LABELS = new Set([
  'Payroll & Benefits',
  'Total Expenses',
]);

const MODEL_NAVY_TEXT_LABELS = new Set([
  'Open Balance',
]);

function columnLetter(columnNumber) {
  let n = columnNumber;
  let letters = '';
  while (n > 0) {
    const remainder = (n - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

function monthsBetween(start, end) {
  if (!start || !end || start.length < 7 || end.length < 7) return null;
  const [startYear, startMonth] = start.split('-').map(Number);
  const [endYear, endMonth] = end.split('-').map(Number);
  if (!startYear || !startMonth || !endYear || !endMonth) return null;
  return (endYear - startYear) * 12 + (endMonth - startMonth);
}

export function expandCumulativeRoas(points, periods) {
  const parsed = (points || [])
    .map((point) => ({
      month: Number(point?.month),
      cumulativeRoasPct: Number(point?.cumulativeRoasPct),
    }))
    .filter((point) => Number.isInteger(point.month) && point.month >= 0 && Number.isFinite(point.cumulativeRoasPct))
    .sort((a, b) => a.month - b.month);
  if (!parsed.length) return [];
  const lastTyped = parsed[parsed.length - 1].month;
  const count = Math.min(MAX_ROAS_PERIODS, Math.max(periods || 0, lastTyped + 1));
  const byMonth = new Map(parsed.map((point) => [point.month, point.cumulativeRoasPct]));
  const values = [];
  let last = 0;
  let step = 0;
  let seen = false;
  for (let index = 0; index < count; index += 1) {
    if (byMonth.has(index)) {
      const value = byMonth.get(index);
      step = seen ? value - last : value;
      last = value;
      seen = true;
    } else if (seen) {
      last += step;
    }
    values.push(seen ? last : '');
  }
  return values;
}

export function repaymentPeriodsUntilPaid(months) {
  const list = [...(months || [])]
    .filter((month) => month?.month)
    .sort((a, b) => a.month.localeCompare(b.month));
  const firstReturn = list.find((month) => (month.returns || 0) > 0);
  if (!firstReturn) return 0;
  let end = firstReturn.month;
  for (const month of list) {
    if (month.month < firstReturn.month) continue;
    end = month.month;
    if ((month.exposureEndingBalance || 0) <= 0.5) break;
  }
  const count = list.filter((month) => month.month >= firstReturn.month && month.month <= end).length;
  return Math.min(count, MAX_ROAS_PERIODS);
}

function pipelineById(pipelineClients) {
  return new Map(
    (pipelineClients || [])
      .filter((client) => client?.customerId)
      .map((client) => [client.customerId, client])
  );
}

function personName(customer, pipeline) {
  return customer?.companyName || pipeline?.companyName || customer?.customerId || pipeline?.customerId || '';
}

function personStage(pipeline) {
  if (!pipeline) return 'Live';
  if (pipeline.future) return 'Future';
  return pipeline.stage || '';
}

function exportPeople(customers, pipelineClients, tenantFilter) {
  const byId = pipelineById(pipelineClients);
  const seen = new Set();
  const people = [];
  (customers || []).forEach((customer) => {
    if (!customer?.customerId || seen.has(customer.customerId)) return;
    if (!shownChartCustomer(customer, pipelineClients, tenantFilter)) return;
    seen.add(customer.customerId);
    people.push({
      customer,
      pipeline: byId.get(customer.customerId) || null,
    });
  });
  return people;
}

function monthValue(months, month, key) {
  const row = (months || []).find((item) => item.month === month);
  const value = row?.[key];
  return value == null || value === 0 ? '' : value;
}

function seriesMonths(people) {
  const keys = new Set();
  people.forEach(({ customer }) => {
    (customer?.months || []).forEach((month) => {
      if (month?.month) keys.add(month.month);
    });
  });
  return [...keys].sort();
}

function modelFill(argb) {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}

function writeMonthHeader(sheet, rowNumber, months, firstColumn) {
  const statusRow = sheet.getRow(rowNumber);
  const labelRow = sheet.getRow(rowNumber + 1);
  const forecastMonth = forecastStartMonth();
  months.forEach((month, index) => {
    const column = firstColumn + index;
    const actual = month < forecastMonth;
    const fill = modelFill(actual ? MODEL_ACTUAL : MODEL_PROJECTED);
    const status = statusRow.getCell(column);
    status.value = actual ? 'Act.' : 'Proj.';
    status.fill = fill;
    status.font = { name: 'Calibri', size: 8, bold: true, color: { argb: MODEL_WHITE } };
    status.alignment = { horizontal: 'center', vertical: 'middle' };
    const label = labelRow.getCell(column);
    label.value = formatMonthLabel(month);
    label.fill = fill;
    label.font = { name: 'Calibri', size: 9, bold: true, color: { argb: MODEL_WHITE } };
    label.alignment = { horizontal: 'center', vertical: 'middle' };
  });
  statusRow.height = 14;
  labelRow.height = 18;
}

function writeMoneyRow(sheet, rowNumber, label, months, valueAt, firstColumn) {
  const row = sheet.getRow(rowNumber);
  row.getCell(1).value = label;
  months.forEach((month, index) => {
    const value = valueAt(month);
    if (value === '' || value == null) return;
    const cell = row.getCell(firstColumn + index);
    cell.value = value;
    cell.numFmt = MODEL_MONEY_FMT;
    cell.font = { name: 'Calibri', size: 9 };
  });
}

function paintModelRow(sheet, rowNumber, monthCount, firstColumn, fill, font) {
  const row = sheet.getRow(rowNumber);
  const lastColumn = Math.max(firstColumn + monthCount - 1, 1);
  for (let column = 1; column <= lastColumn; column += 1) {
    const cell = row.getCell(column);
    cell.fill = modelFill(fill);
    cell.font = font;
    cell.alignment = { vertical: 'middle', horizontal: column === 1 ? 'left' : 'center' };
  }
}

function styleModelValueRow(sheet, rowNumber, monthCount, firstColumn, labelFont, valueFill, valueFont) {
  const row = sheet.getRow(rowNumber);
  const label = row.getCell(1);
  label.font = labelFont;
  label.alignment = { vertical: 'middle', horizontal: 'left' };
  for (let index = 0; index < monthCount; index += 1) {
    const cell = row.getCell(firstColumn + index);
    cell.fill = modelFill(valueFill);
    cell.font = valueFont;
    if (cell.value != null && cell.value !== '') cell.numFmt = MODEL_MONEY_FMT;
  }
}

function styleModelSheet(sheet, { tabColor, monthCount, firstColumn }) {
  sheet.properties.tabColor = { argb: tabColor };
  const lastColumn = Math.max(firstColumn + monthCount - 1, 1);
  const title = sheet.getRow(1);
  title.height = 22;
  for (let column = 1; column <= lastColumn; column += 1) {
    const cell = title.getCell(column);
    cell.fill = modelFill(MODEL_NAVY);
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: MODEL_WHITE } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  }
  if (lastColumn > 1) sheet.mergeCells(1, 1, 1, lastColumn);

  const navyFont = { name: 'Calibri', size: 9, bold: true, color: { argb: MODEL_NAVY } };
  const whiteFont = { name: 'Calibri', size: 9, bold: true, color: { argb: MODEL_WHITE } };
  const greenFont = { name: 'Calibri', size: 9, bold: true, color: { argb: MODEL_GREEN_FONT } };
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber <= 3) return;
    const label = row.getCell(1).value;
    if (typeof label !== 'string' || !label) return;
    if (MODEL_SECTION_LABELS.has(label)) {
      paintModelRow(sheet, rowNumber, monthCount, firstColumn, MODEL_NAVY, whiteFont);
      return;
    }
    if (MODEL_GRAY_LABELS.has(label)) {
      paintModelRow(sheet, rowNumber, monthCount, firstColumn, MODEL_GRAY_FILL, {
        name: 'Calibri', size: 9, bold: true, color: { argb: MODEL_GRAY_FONT },
      });
      return;
    }
    if (MODEL_GRAY_TOTAL_LABELS.has(label)) {
      styleModelValueRow(sheet, rowNumber, monthCount, firstColumn, navyFont, 'FFD9D9D9', navyFont);
      row.getCell(1).fill = modelFill('FFD9D9D9');
      return;
    }
    if (MODEL_GREEN_LABELS.has(label)) {
      styleModelValueRow(sheet, rowNumber, monthCount, firstColumn, greenFont, MODEL_GREEN_FILL, greenFont);
      return;
    }
    if (MODEL_TOTAL_LABELS.has(label)) {
      styleModelValueRow(sheet, rowNumber, monthCount, firstColumn, navyFont, MODEL_TOTAL_FILL, navyFont);
      return;
    }
    if (MODEL_NAVY_TEXT_LABELS.has(label)) {
      row.getCell(1).font = navyFont;
      return;
    }
    row.getCell(1).font = { name: 'Calibri', size: 9, color: { argb: 'FF000000' } };
    row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
  });

  sheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 3, showGridLines: true }];
  sheet.getColumn(1).width = 46;
  for (let index = 0; index < monthCount; index += 1) {
    sheet.getColumn(firstColumn + index).width = 12;
  }
}

function styleHeader(row) {
  row.font = { bold: true };
}

function setWidths(sheet, monthCount, firstWidth) {
  sheet.getColumn(1).width = firstWidth;
  for (let column = 2; column <= monthCount + 1; column += 1) {
    sheet.getColumn(column).width = 14;
  }
}

export function writeFundingSheet(sheet, { customers, pipelineClients, months: windowMonths, tenantFilter }) {
  const people = exportPeople(customers, pipelineClients, tenantFilter);
  const months = (windowMonths || []).filter(Boolean).length
    ? [...windowMonths].filter(Boolean).sort()
    : seriesMonths(people);
  sheet.getCell(1, 1).value = 'Funding amount';
  sheet.getCell(1, 1).font = { bold: true };
  const header = sheet.getRow(2);
  header.getCell(1).value = 'Customer';
  header.getCell(2).value = 'Stage';
  months.forEach((month, index) => {
    header.getCell(3 + index).value = formatMonthLabel(month);
  });
  styleHeader(header);
  people.forEach((person, index) => {
    const row = sheet.getRow(3 + index);
    row.getCell(1).value = personName(person.customer, person.pipeline);
    row.getCell(2).value = personStage(person.pipeline);
    months.forEach((month, monthIndex) => {
      const value = monthValue(person.customer?.months, month, 'growthInvestments');
      if (value === '') return;
      const cell = row.getCell(3 + monthIndex);
      cell.value = value;
      cell.numFmt = EXCEL_MONEY_NUM_FMT;
    });
  });
  sheet.getColumn(1).width = 28;
  sheet.getColumn(2).width = 18;
  for (let column = 3; column <= months.length + 2; column += 1) {
    sheet.getColumn(column).width = 14;
  }
}

function repaymentVectorFor(pipeline, settings) {
  if (pipeline?.useGenericCurve) return settings?.genericVector || [];
  return pipeline?.repaymentVector || [];
}

export function writePipelineRepaymentSheet(sheet, { customers, pipelineClients, settings, tenantFilter }) {
  const people = exportPeople(customers, pipelineClients, tenantFilter).filter((person) => person.pipeline);
  const seriesById = new Map(
    (customers || []).filter((customer) => customer?.customerId).map((customer) => [customer.customerId, customer])
  );
  const expanded = people.map((person) => {
    const series = seriesById.get(person.pipeline.customerId);
    const periods = repaymentPeriodsUntilPaid(series?.months);
    return expandCumulativeRoas(repaymentVectorFor(person.pipeline, settings), periods);
  });
  const periodCount = expanded.reduce((max, row) => Math.max(max, row.length), 0);
  const header = sheet.getRow(1);
  header.getCell(1).value = 'Customer';
  header.getCell(2).value = 'Stage';
  for (let period = 0; period < periodCount; period += 1) {
    header.getCell(3 + period).value = `M${period}`;
  }
  styleHeader(header);
  people.forEach((person, index) => {
    const row = sheet.getRow(2 + index);
    row.getCell(1).value = personName(person.customer, person.pipeline);
    row.getCell(2).value = personStage(person.pipeline);
    expanded[index].forEach((value, period) => {
      if (value === '') return;
      const cell = row.getCell(3 + period);
      cell.value = value;
      cell.numFmt = ROAS_NUM_FMT;
    });
  });
  sheet.getColumn(1).width = 28;
  sheet.getColumn(2).width = 18;
}

function cohortKey(item) {
  return `${item.source || ''}|${item.cohortMonth || ''}|${item.marketingMonth || ''}|${item.label || ''}`;
}

// marketingMonthOf is the marketing cohort a funding was sized from. The server
// sends it (marketingMonth); results saved before it did fall back to the month
// before the funding.
export function marketingMonthOf(item) {
  if (item?.marketingMonth) return item.marketingMonth;
  return shiftMonth(item?.cohortMonth || '', -1);
}

function cohortLabel(item) {
  if (item?.label) return item.label;
  if (item?.cohortMonth) return item.cohortMonth;
  if (item?.source === 'funded_sql') return 'Funded cohorts';
  return 'Cohort';
}

function shiftMonth(month, delta) {
  const match = /^(\d{4})-(\d{2})$/.exec(month || '');
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1 + delta, 1));
  const year = date.getUTCFullYear();
  const shifted = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${shifted}`;
}

export function cohortRepaymentSchedule(customer) {
  const months = [...(customer?.months || [])].sort((a, b) => a.month.localeCompare(b.month));
  const fundingOwners = new Map();
  months.forEach((month) => {
    (month.returnsBreakdown || []).forEach((item) => {
      if (!item?.cohortMonth) return;
      const owners = fundingOwners.get(item.cohortMonth) || new Set();
      owners.add(cohortKey(item));
      fundingOwners.set(item.cohortMonth, owners);
    });
  });
  const fundingByMonth = new Map(
    months
      .filter((month) => (month.growthInvestments || 0) > 0)
      .map((month) => [month.month, month.growthInvestments])
  );
  const cohorts = new Map();
  months.forEach((month) => {
    (month.returnsBreakdown || []).forEach((item) => {
      const key = cohortKey(item);
      if (!cohorts.has(key)) {
        const fundingMonth = item.cohortMonth || '';
        const owners = fundingMonth ? fundingOwners.get(fundingMonth) : null;
        const funding = Number(item.cohortFunding) > 0
          ? Number(item.cohortFunding)
          : (owners && owners.size === 1 ? fundingByMonth.get(fundingMonth) || null : null);
        cohorts.set(key, {
          key,
          label: cohortLabel(item),
          hasMarketingMonth: Boolean(item.marketingMonth),
          cohortMonth: marketingMonthOf(item),
          fundingMonth,
          funding,
          byPeriod: new Map(),
        });
      }
      const period = item.roasPeriod != null
        ? item.roasPeriod
        : monthsBetween(item.cohortMonth, month.month);
      if (period == null || period < 0) return;
      const cohort = cohorts.get(key);
      cohort.byPeriod.set(period, (cohort.byPeriod.get(period) || 0) + (item.amount || 0));
    });
  });
  // An installment without a repayment cohort shares its funding month with real
  // cohorts: show its own label instead of guessing "funding month - 1".
  const fundingWithCohort = new Set(
    [...cohorts.values()].filter((cohort) => cohort.hasMarketingMonth).map((cohort) => cohort.fundingMonth)
  );
  cohorts.forEach((cohort) => {
    if (!cohort.hasMarketingMonth && fundingWithCohort.has(cohort.fundingMonth)) cohort.cohortMonth = '';
  });
  return [...cohorts.values()]
    .sort((left, right) => String(left.fundingMonth).localeCompare(String(right.fundingMonth))
      || String(left.cohortMonth).localeCompare(String(right.cohortMonth))
      || String(left.label).localeCompare(String(right.label)))
    .map((cohort) => {
      const periods = [...cohort.byPeriod.keys()];
      const maxPeriod = periods.length ? Math.max(...periods) : -1;
      const amounts = [];
      const roasPct = [];
      for (let period = 0; period <= maxPeriod && period < MAX_ROAS_PERIODS; period += 1) {
        const amount = cohort.byPeriod.get(period) || 0;
        amounts.push(amount || null);
        roasPct.push(amount && cohort.funding ? (amount / cohort.funding) * 100 : null);
      }
      return {
        key: cohort.key,
        label: cohort.label,
        cohortMonth: cohort.cohortMonth,
        fundingMonth: cohort.fundingMonth,
        funding: cohort.funding,
        amounts,
        roasPct,
      };
    });
}

export function twelveNetRows(customers, months) {
  const monthKeys = (months || []).map((month) => month.month).filter(Boolean);
  const rows = (customers || []).map((customer) => {
    const byMonth = new Map((customer.months || []).map((month) => [month.month, month]));
    return {
      customerId: customer.customerId,
      name: customer.companyName || customer.customerId || 'Customer',
      amounts: monthKeys.map((key) => {
        const month = byMonth.get(key);
        if (!month) return 0;
        return roundMoney((Number(month.returns) || 0) - (Number(month.growthInvestments) || 0));
      }),
    };
  });
  const totals = monthKeys.map((_, index) => roundMoney(
    rows.reduce((sum, row) => sum + row.amounts[index], 0)
  ));
  return { monthKeys, rows, totals };
}

function roundMoney(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

// One row per cohort: funding month + marketing month. Lines without a marketing
// month (results saved before the server sent it) stay one row per funding month.
function contributionIdentity(item) {
  if (item?.cohortMonth && item?.marketingMonth) return `month|${item.cohortMonth}|${item.marketingMonth}`;
  if (item?.cohortMonth) return `month|${item.cohortMonth}`;
  return cohortKey(item);
}

// A row is labelled with its marketing cohort. A funding-month row without one
// (older results) is labelled with every marketing cohort that reaches it.
function contributionRow(item) {
  const fundingMonth = item?.cohortMonth || '';
  const row = {
    key: contributionIdentity(item),
    label: fundingMonth ? formatMonthLabel(shiftMonth(fundingMonth, -1)) : cohortLabel(item),
    fundingMonth,
    marketingMonths: new Set(),
    beginning: 0,
    fee: 0,
    repayment: 0,
    ending: 0,
    balance: 0,
  };
  noteMarketingMonth(row, item);
  return row;
}

function noteMarketingMonth(row, item) {
  if (!row.fundingMonth || !item?.marketingMonth || row.marketingMonths.has(item.marketingMonth)) return;
  row.marketingMonths.add(item.marketingMonth);
  row.label = [...row.marketingMonths].sort().map(formatMonthLabel).join(' & ');
}

function materialAmount(value) {
  return Math.abs(value) >= 0.5;
}

function activeContribution(row) {
  return [row.beginning, row.fee, row.repayment, row.ending].some(materialAmount);
}

// Actual months store one exposure total. The fee is charged on each cohort's opening
// exposure, so that total is split by the fee each cohort earned this month.
function exposureRowsFromFeeShare(month) {
  if (month?.exposureBeginningBalance == null || month?.exposureEndingBalance == null) return null;
  const rows = new Map();
  const add = (item, field) => {
    const amount = Number(item?.amount) || 0;
    if (!amount) return;
    const key = contributionIdentity(item);
    const row = rows.get(key) || contributionRow(item);
    noteMarketingMonth(row, item);
    row[field] = roundMoney(row[field] + amount);
    rows.set(key, row);
  };
  (month.feeBreakdown || []).forEach((item) => add(item, 'fee'));
  (month.returnsBreakdown || []).forEach((item) => add(item, 'repayment'));
  const withFee = [...rows.values()].filter((row) => materialAmount(row.fee));
  const feeSum = withFee.reduce((sum, row) => sum + row.fee, 0);
  if (!materialAmount(feeSum)) return null;
  if (month.fee != null && materialAmount(Number(month.fee) - feeSum)) return null;
  if ([...rows.values()].some((row) => materialAmount(row.repayment) && !materialAmount(row.fee))) return null;

  const beginningTotal = Number(month.exposureBeginningBalance);
  let allocated = 0;
  withFee.forEach((row, index) => {
    let share = roundMoney(beginningTotal * row.fee / feeSum);
    if (index === withFee.length - 1) share = roundMoney(beginningTotal - allocated);
    allocated = roundMoney(allocated + share);
    row.beginning = share;
    row.ending = roundMoney(row.beginning + row.fee - row.repayment);
  });

  const reportedEnding = Number(month.exposureEndingBalance);
  const explainedEnding = roundMoney(beginningTotal + feeSum - [...rows.values()].reduce((sum, row) => sum + row.repayment, 0));
  const netNew = roundMoney(reportedEnding - explainedEnding);
  if (materialAmount(netNew)) {
    const fundedThisMonth = [...rows.values()].filter((row) => row.fundingMonth === month.month);
    if (fundedThisMonth.length === 1) {
      fundedThisMonth[0].ending = roundMoney(fundedThisMonth[0].ending + netNew);
    } else if (fundedThisMonth.length === 0) {
      const row = contributionRow({
        source: 'projected',
        cohortMonth: month.month,
        label: `Projected ${month.month}`,
      });
      row.ending = netNew;
      rows.set(row.key, row);
    } else {
      return null;
    }
  }

  const endingSum = [...rows.values()].reduce((sum, row) => sum + row.ending, 0);
  if (materialAmount(endingSum - reportedEnding)) return null;
  return [...rows.values()].filter(activeContribution).map(({ balance, marketingMonths, ...row }) => row);
}

// A funding month can hold cohorts with a marketing month and installments
// without one (fixed installments have no repayment cohort). The rows without one
// are named by their funding month, so they never read like a real cohort.
function labelRowsWithoutCohort(rows) {
  const withCohort = new Set(
    rows.filter((row) => String(row.key).split('|').length === 3).map((row) => row.fundingMonth)
  );
  return rows.map((row) => (
    row.fundingMonth && row.key === `month|${row.fundingMonth}` && withCohort.has(row.fundingMonth)
      ? { ...row, label: `${formatMonthLabel(row.fundingMonth)} funding, no cohort` }
      : row
  ));
}

export function monthCohortContributions(customer, calendarMonth) {
  return labelRowsWithoutCohort(monthCohortRows(customer, calendarMonth));
}

function monthCohortRows(customer, calendarMonth) {
  const months = [...(customer?.months || [])].sort((left, right) => left.month.localeCompare(right.month));
  const target = months.find((item) => item.month === calendarMonth);
  if (!target) return [];

  const cohorts = new Map();
  const ensure = (item) => {
    const key = contributionIdentity(item);
    if (!cohorts.has(key)) cohorts.set(key, contributionRow(item));
    const row = cohorts.get(key);
    noteMarketingMonth(row, item);
    return row;
  };

  // Each cohort's own funded amount, by funding month and marketing month.
  const fundingSplit = new Map();
  months.forEach((month) => {
    [...(month.returnsBreakdown || []), ...(month.feeBreakdown || [])].forEach((item) => {
      const amount = Number(item?.cohortFunding) || 0;
      if (!item?.cohortMonth || !item?.marketingMonth || amount <= 0) return;
      const byMarketing = fundingSplit.get(item.cohortMonth) || new Map();
      byMarketing.set(item.marketingMonth, amount);
      fundingSplit.set(item.cohortMonth, byMarketing);
    });
  });

  let selected = null;
  months.forEach((month) => {
    const beginning = new Map();
    cohorts.forEach((row, key) => beginning.set(key, row.balance));

    const feeByKey = new Map();
    const repaymentByKey = new Map();
    const addActivity = (store, item) => {
      const amount = Number(item?.amount) || 0;
      if (!amount) return;
      const row = ensure(item);
      store.set(row.key, roundMoney((store.get(row.key) || 0) + amount));
    };
    const listedFee = (month.feeBreakdown || []).reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);
    if (materialAmount(listedFee)) {
      (month.feeBreakdown || []).forEach((item) => addActivity(feeByKey, item));
    } else {
      const fee = Number(month.fee) || 0;
      const open = [...beginning.entries()].filter(([, amount]) => amount > 0.01);
      const opening = open.reduce((sum, [, amount]) => sum + amount, 0);
      if (fee && opening > 0.01) {
        let allocated = 0;
        open.forEach(([key, amount], index) => {
          let share = roundMoney(fee * amount / opening);
          if (index === open.length - 1) share = roundMoney(fee - allocated);
          allocated = roundMoney(allocated + share);
          feeByKey.set(key, share);
        });
      }
    }
    (month.returnsBreakdown || []).forEach((item) => addActivity(repaymentByKey, item));

    cohorts.forEach((row, key) => {
      row.balance = roundMoney(row.balance + (feeByKey.get(key) || 0) - (repaymentByKey.get(key) || 0));
    });

    const funding = Number(month.growthInvestments) || 0;
    if (funding) {
      // Split the month's funding between the cohorts funded in it, by each
      // cohort's own funded amount. Whatever is not matched stays on one
      // funding-month row, so the roll-forward still adds up.
      let placed = 0;
      (fundingSplit.get(month.month) || new Map()).forEach((amount, marketingMonth) => {
        const row = ensure({ cohortMonth: month.month, marketingMonth });
        row.balance = roundMoney(row.balance + amount);
        placed = roundMoney(placed + amount);
      });
      const rest = roundMoney(funding - placed);
      if (materialAmount(rest)) {
        const row = ensure({ cohortMonth: month.month });
        row.balance = roundMoney(row.balance + rest);
      }
    }

    if (month.month !== calendarMonth || selected) return;
    const ending = new Map();
    cohorts.forEach((row, key) => ending.set(key, row.balance));
    selected = {
      month,
      beginning,
      feeByKey,
      repaymentByKey,
      ending,
    };
  });
  if (!selected) return [];

  const rows = [];
  cohorts.forEach((row, key) => {
    const beginning = selected.beginning.get(key) || 0;
    const fee = selected.feeByKey.get(key) || 0;
    const repayment = selected.repaymentByKey.get(key) || 0;
    const ending = selected.ending.get(key) || 0;
    if (!activeContribution({ beginning, fee, repayment, ending })) return;
    rows.push({
      key,
      label: row.label,
      fundingMonth: row.fundingMonth,
      beginning,
      fee,
      repayment,
      ending,
    });
  });

  const reportedBeginning = selected.month.exposureBeginningBalance;
  const reportedEnding = selected.month.exposureEndingBalance;
  const rolledBeginning = rows.reduce((sum, row) => sum + row.beginning, 0);
  const rolledEnding = rows.reduce((sum, row) => sum + row.ending, 0);
  const rollMatches = (reportedBeginning == null || !materialAmount(rolledBeginning - Number(reportedBeginning)))
    && (reportedEnding == null || !materialAmount(rolledEnding - Number(reportedEnding)));
  if (!rollMatches) {
    const shared = exposureRowsFromFeeShare(selected.month);
    if (shared) {
      return shared.sort((left, right) => (
        String(left.fundingMonth).localeCompare(String(right.fundingMonth))
        || String(left.label).localeCompare(String(right.label))
      ));
    }
  }

  const plug = (reported, field) => (reported == null ? 0 : roundMoney(Number(reported) - rows.reduce((sum, row) => sum + row[field], 0)));
  const other = {
    key: '__other__',
    label: 'Other',
    fundingMonth: '',
    beginning: plug(selected.month.exposureBeginningBalance, 'beginning'),
    fee: plug(selected.month.fee, 'fee'),
    repayment: plug(selected.month.returns, 'repayment'),
    ending: plug(selected.month.exposureEndingBalance, 'ending'),
  };
  if (['beginning', 'fee', 'repayment', 'ending'].some((field) => materialAmount(other[field]))) {
    rows.push(other);
  }

  return rows.sort((left, right) => {
    if (left.key === '__other__') return 1;
    if (right.key === '__other__') return -1;
    return String(left.fundingMonth).localeCompare(String(right.fundingMonth))
      || String(left.label).localeCompare(String(right.label));
  });
}

export function liveCohortRoasRows(customer) {
  return cohortRepaymentSchedule(customer).map((cohort) => ({
    label: cohort.label,
    fundingMonth: cohort.fundingMonth,
    funding: cohort.funding || '',
    roas: cohort.roasPct.map((value) => (value == null ? '' : value)),
  }));
}

export function writeLiveRepaymentSheet(sheet, { customers, pipelineClients, tenantFilter }) {
  const live = exportPeople(customers, pipelineClients, tenantFilter).filter((person) => person.customer && !person.pipeline);
  let rowNumber = 1;
  live.forEach((person) => {
    const cohorts = liveCohortRoasRows(person.customer);
    const periodCount = cohorts.reduce((max, cohort) => Math.max(max, cohort.roas.length), 0);
    const title = sheet.getRow(rowNumber);
    title.getCell(1).value = personName(person.customer, null);
    title.font = { bold: true };
    const header = sheet.getRow(rowNumber + 1);
    header.getCell(1).value = 'Cohort';
    header.getCell(2).value = 'Funding month';
    header.getCell(3).value = 'Funding';
    for (let period = 0; period < periodCount; period += 1) {
      header.getCell(4 + period).value = `M${period}`;
    }
    styleHeader(header);
    cohorts.forEach((cohort, index) => {
      const row = sheet.getRow(rowNumber + 2 + index);
      row.getCell(1).value = cohort.label;
      row.getCell(2).value = cohort.fundingMonth ? formatMonthLabel(cohort.fundingMonth) : '';
      if (cohort.funding) {
        row.getCell(3).value = cohort.funding;
        row.getCell(3).numFmt = EXCEL_MONEY_NUM_FMT;
      }
      cohort.roas.forEach((value, period) => {
        if (value === '' || value == null) return;
        const cell = row.getCell(4 + period);
        cell.value = value;
        cell.numFmt = ROAS_NUM_FMT;
      });
    });
    rowNumber += 2 + Math.max(cohorts.length, 1) + 1;
  });
  sheet.getColumn(1).width = 28;
  sheet.getColumn(2).width = 16;
  sheet.getColumn(3).width = 16;
}

function sumSeries(people, month, key) {
  return people.reduce((total, person) => {
    const row = (person.customer?.months || []).find((item) => item.month === month);
    return total + (row?.[key] || 0);
  }, 0);
}

export function buildSourcesAndUsesRows({ customers, pipelineClients, months: windowMonths, tenantFilter }) {
  const people = exportPeople(customers, pipelineClients, tenantFilter).filter((person) => person.customer);
  const pipeline = people.filter((person) => person.pipeline);
  const live = people.filter((person) => !person.pipeline);
  const window = [...(windowMonths || [])].filter(Boolean).sort();
  const months = window.length ? window : seriesMonths(people);
  const across = (group, key) => months.map((month) => {
    const total = sumSeries(group, month, key);
    return total || null;
  });
  const combine = (left, right, sign) => months.map((_, index) => {
    const value = (left[index] || 0) + sign * (right[index] || 0);
    return value || null;
  });
  const pipelineGrowth = across(pipeline, 'growthInvestments');
  const pipelineReturns = across(pipeline, 'returns');
  const liveGrowth = across(live, 'growthInvestments');
  const liveReturns = across(live, 'returns');
  const pipelineFee = across(pipeline, 'fee');
  const liveFee = across(live, 'fee');
  const liveFacilityFee = across(live, 'facilityFee');
  const totalGrowth = combine(pipelineGrowth, liveGrowth, 1);
  const totalReturns = combine(pipelineReturns, liveReturns, 1);
  const totalFee = months.map((_, index) => {
    const value = (pipelineFee[index] || 0) + (liveFee[index] || 0) + (liveFacilityFee[index] || 0);
    return value || null;
  });
  const amount = (label, values) => ({ kind: 'amount', label, values });
  const section = (label) => ({ kind: 'section', label });
  const rows = [
    section('① CUSTOMER VIEW'),
    section('PIPELINE'),
    amount('Growth Investments (cash out)', pipelineGrowth),
    amount('Returns (cash in, fees deducted)', pipelineReturns),
    section('LIVE COMPANIES'),
    amount('Growth Investments (cash out)', liveGrowth),
    amount('Returns (cash in, fees deducted)', liveReturns),
    section('TOTAL'),
    amount('Total Growth Investments (cash out)', totalGrowth),
    amount('Total Returns (cash in, fees deducted)', totalReturns),
    amount('Net capital needed', combine(totalGrowth, totalReturns, -1)),
    section('FEE SUMMARY'),
    amount('Pipeline - Fee', pipelineFee),
    amount('Live - Fee', liveFee),
    amount('Live - Facility Fee', liveFacilityFee),
    amount('Total Fee (yield)', totalFee),
  ];
  return { months, rows };
}

export function writeSourcesAndUsesSheet(sheet, { customers, pipelineClients, months: windowMonths, tenantFilter }) {
  const people = exportPeople(customers, pipelineClients, tenantFilter).filter((person) => person.customer);
  const pipeline = people.filter((person) => person.pipeline);
  const live = people.filter((person) => !person.pipeline);
  const window = [...(windowMonths || [])].filter(Boolean).sort();
  const months = window.length ? window : seriesMonths(people);
  sheet.getCell(1, 1).value = 'Sources & Uses — Monthly Cash Flow';
  sheet.getCell(1, 1).font = { bold: true };
  sheet.getCell(2, 1).value = '';
  writeMonthHeader(sheet, 2, months, 2);
  sheet.getCell(5, 1).value = '① CUSTOMER VIEW';
  sheet.getCell(5, 1).font = { bold: true };
  sheet.getCell(6, 1).value = 'PIPELINE';
  sheet.getCell(6, 1).font = { bold: true };
  writeMoneyRow(sheet, 7, 'Growth Investments (cash out)', months, (month) => {
    const value = sumSeries(pipeline, month, 'growthInvestments');
    return value || '';
  }, 2);
  writeMoneyRow(sheet, 8, 'Returns (cash in, fees deducted)', months, (month) => {
    const value = sumSeries(pipeline, month, 'returns');
    return value || '';
  }, 2);
  sheet.getCell(10, 1).value = 'LIVE COMPANIES';
  sheet.getCell(10, 1).font = { bold: true };
  writeMoneyRow(sheet, 11, 'Growth Investments (cash out)', months, (month) => {
    const value = sumSeries(live, month, 'growthInvestments');
    return value || '';
  }, 2);
  writeMoneyRow(sheet, 12, 'Returns (cash in, fees deducted)', months, (month) => {
    const value = sumSeries(live, month, 'returns');
    return value || '';
  }, 2);
  sheet.getCell(14, 1).value = 'TOTAL';
  sheet.getCell(14, 1).font = { bold: true };
  const totalGrowth = sheet.getRow(15);
  const totalReturns = sheet.getRow(16);
  const netCapital = sheet.getRow(17);
  totalGrowth.getCell(1).value = 'Total Growth Investments (cash out)';
  totalReturns.getCell(1).value = 'Total Returns (cash in, fees deducted)';
  netCapital.getCell(1).value = 'Net capital needed';
  months.forEach((_, index) => {
    const column = columnLetter(2 + index);
    totalGrowth.getCell(2 + index).value = { formula: `${column}7+${column}11` };
    totalReturns.getCell(2 + index).value = { formula: `${column}8+${column}12` };
    netCapital.getCell(2 + index).value = { formula: `${column}15-${column}16` };
    [totalGrowth, totalReturns, netCapital].forEach((row) => {
      row.getCell(2 + index).numFmt = MODEL_MONEY_FMT;
    });
  });
  sheet.getCell(19, 1).value = 'FEE SUMMARY';
  sheet.getCell(19, 1).font = { bold: true };
  writeMoneyRow(sheet, 20, 'Pipeline - Fee', months, (month) => {
    const value = sumSeries(pipeline, month, 'fee');
    return value || '';
  }, 2);
  writeMoneyRow(sheet, 21, 'Live - Fee', months, (month) => {
    const value = sumSeries(live, month, 'fee');
    return value || '';
  }, 2);
  writeMoneyRow(sheet, 22, 'Live - Facility Fee', months, (month) => {
    const value = sumSeries(live, month, 'facilityFee');
    return value || '';
  }, 2);
  const totalFee = sheet.getRow(23);
  totalFee.getCell(1).value = 'Total Fee (yield)';
  months.forEach((_, index) => {
    const column = columnLetter(2 + index);
    const cell = totalFee.getCell(2 + index);
    cell.value = { formula: `${column}20+${column}21+${column}22` };
    cell.numFmt = MODEL_MONEY_FMT;
  });
  styleModelSheet(sheet, { tabColor: MODEL_PROJECTED, monthCount: months.length, firstColumn: 2 });
}
